import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Loader2, UserPlus } from 'lucide-react';
import { toast } from 'sonner';

const AddUserModal = ({ open, onOpenChange, onCreated }) => {
  const navigate = useNavigate();
  const [role, setRole] = useState('talent');
  const [adminRole, setAdminRole] = useState('talent_admin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [company, setCompany] = useState('');
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setEmail(''); setPassword(''); setFirstName(''); setLastName('');
    setPhone(''); setCompany(''); setRole('talent'); setAdminRole('talent_admin');
  };

  const submit = async () => {
    if (!email || !password) { toast.error('Email + password required'); return; }
    if (password.length < 8) { toast.error('Password must be 8+ chars'); return; }
    setSaving(true);
    try {
      const payload = {
        email: email.trim().toLowerCase(),
        password,
        role,
        first_name: firstName || null,
        last_name: lastName || null,
        phone: phone || null,
      };
      if (role === 'client' || role === 'agency') payload.company_name = company || null;
      if (role === 'admin') payload.admin_role = adminRole;
      const res = await api.post('/admin/users', payload);
      toast.success(`${role} account created`);
      reset();
      onOpenChange(false);
      if (onCreated) onCreated(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="add-user-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-cyan-500" /> Add User
          </DialogTitle>
          <DialogDescription className="text-xs text-gray-500">
            Create a new account. The user will receive a welcome email with sign-in instructions.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Role *</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="talent">Talent</SelectItem>
                  <SelectItem value="client">Client</SelectItem>
                  <SelectItem value="agency">Agency</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="ai_talent">AI Talent (Avatar)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {role === 'admin' && (
              <div className="space-y-1.5">
                <Label>Admin Role *</Label>
                <Select value={adminRole} onValueChange={setAdminRole}>
                  <SelectTrigger data-testid="admin-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="talent_admin">Talent Admin</SelectItem>
                    <SelectItem value="client_admin">Client Admin</SelectItem>
                    <SelectItem value="finance_admin">Finance Admin</SelectItem>
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {role === 'ai_talent' ? (
            <div className="rounded-lg border border-violet-200 bg-violet-50 p-4 text-sm text-violet-800" data-testid="ai-talent-panel">
              AI talent use a dedicated setup (name, photo, bio, skills, services, pricing &amp; booking routing) — no email, phone or password needed. Continue to the AI Talent manager to add and publish your avatar to Talent Discovery, right alongside your existing AI ambassadors.
            </div>
          ) : (
          <>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>First Name</Label>
              <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} data-testid="first-name-input" />
            </div>
            <div className="space-y-1.5"><Label>Last Name</Label>
              <Input value={lastName} onChange={(e) => setLastName(e.target.value)} data-testid="last-name-input" />
            </div>
          </div>

          {(role === 'client' || role === 'agency') && (
            <div className="space-y-1.5"><Label>Company Name</Label>
              <Input value={company} onChange={(e) => setCompany(e.target.value)} data-testid="company-name-input" />
            </div>
          )}

          <div className="space-y-1.5"><Label>Email *</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="email-input" />
          </div>
          <div className="space-y-1.5"><Label>Phone</Label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 305 555 0123" data-testid="phone-input" />
          </div>
          <div className="space-y-1.5"><Label>Temporary Password *</Label>
            <Input type="text" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min 8 characters" data-testid="password-input" />
            <p className="text-[11px] text-gray-500">User can reset it after first login.</p>
          </div>
          </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          {role === 'ai_talent' ? (
            <Button onClick={() => { onOpenChange(false); navigate('/admin/ai-talent'); }} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="goto-ai-avatar-btn">
              Continue to AI Talent setup
            </Button>
          ) : (
            <Button onClick={submit} disabled={saving} className="bg-cyan-500 hover:bg-cyan-600 text-white" data-testid="create-user-submit-btn">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating…</> : 'Create User'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddUserModal;
